import React from 'react'
import { Helmet } from 'react-helmet';
import utils from '../utils';

function PaymentsnBillingPage() {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    return (
        <div>
            <Helmet>
                <title>Payments & Billings - {utils.siteName}</title>
                <link rel="canonical" href={url} />
            </Helmet>
            <div>
                <header className="hero-bg text-white py-16">
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl md:text-6xl font-black">Payments & Billings</h1>
                        <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">This section outlines how payments, billing, and financial transactions are handled on {utils.siteName} — including accepted methods, pricing, fees, and your responsibilities as a user.</p>
                        <div className="mt-6 mx-auto w-20 h-1.5 bg-secondary rounded-full"></div>
                    </div>
                </header>
                <section className="py-16 lg:py-24">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto bg-white dark:bg-slate-900 p-8 md:p-12 rounded-lg shadow-xl">
                            <div className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 uppercase">
                                    Koova Payments & Billing Policy
                                </h2>
                                <p>
                                    This Payments & Billing Policy explains how payments, pricing, billing, and related issues are handled on the Koova platform.
                                </p>
                                <p>
                                    By using Koova and placing an order, you agree to this Policy in addition to the Koova Terms & Conditions.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    1. Platform Role
                                </h2>
                                <p>
                                    Koova operates as an online marketplace that facilitates transactions between buyers and independent sellers.
                                </p>
                                <p>Unless expressly stated:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Sellers set prices, offer products or services, and are responsible for fulfillment</li>
                                    <li>Koova does not manufacture, own, or sell listed products</li>
                                    <li>Payments are processed through third-party payment service providers</li>
                                </ul>
                                <p>
                                    Koova may collect payments on behalf of sellers where permitted by law.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    2. Accepted Payment Methods
                                </h2>
                                <p>
                                    Koova may support one or more of the following payment methods, depending on location and availability:
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Debit and credit cards</li>
                                    <li>Mobile money services</li>
                                    <li>Bank transfers</li>
                                    <li>Digital wallets</li>
                                    <li>Any other payment method made available on the Platform</li>
                                </ul>
                                <p>
                                    Koova reserves the right to add, remove, or restrict payment methods at any time.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    3. Pricing and Currency
                                </h2>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>All prices are displayed in the applicable local currency unless otherwise stated</li>
                                    <li>Prices include applicable taxes unless specified as exclusive</li>
                                    <li>Sellers are responsible for the accuracy of product pricing</li>
                                </ul>
                                <p>
                                    Koova may correct pricing errors at any time and may cancel orders affected by obvious pricing mistakes.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    4. Taxes and Fees
                                </h2>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Applicable taxes, levies, or government charges may be added at checkout</li>
                                    <li>Sellers are responsible for determining and remitting any taxes required by law</li>
                                    <li>Koova may charge service, transaction, or platform fees, which will be disclosed before payment</li>
                                </ul>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    5. Payment Authorization and Confirmation
                                </h2>
                                <p>
                                    By submitting payment details, you authorize Koova or its payment partners to:
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Charge the total order amount</li>
                                    <li>Perform verification and anti-fraud checks</li>
                                    <li>Share necessary transaction data with sellers and payment providers</li>
                                </ul>
                                <p>
                                    An order is not confirmed until payment authorization is successfully completed.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    6. Failed, Reversed, or Declined Payments
                                </h2>
                                <p>Koova is not responsible for payments that fail due to:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Insufficient funds</li>
                                    <li>Incorrect payment details</li>
                                    <li>Bank or mobile money provider issues</li>
                                    <li>Network or system failures beyond Koova’s control</li>
                                </ul>
                                <p>
                                    Orders associated with failed or reversed payments may be cancelled automatically.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    7. Billing Information
                                </h2>
                                <p>
                                    You agree to provide accurate, complete, and up-to-date billing information.
                                </p>
                                <p>
                                    Koova is not liable for delays, losses, or errors resulting from inaccurate billing details provided by you.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    8. Installments and Partial Payments
                                </h2>
                                <p>Where offered, installment or partial payment options:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Are subject to eligibility and third-party provider terms</li>
                                    <li>May involve additional fees or interest</li>
                                    <li>Are governed by separate agreements with the payment provider</li>
                                </ul>
                                <p>
                                    Koova is not responsible for disputes arising from third-party financing arrangements.
                                </p>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    9. Refunds and Chargebacks
                                </h2>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Refunds are governed by the <em>Koova Returns & Refunds Policy</em></li>
                                    <li>Chargebacks or payment disputes initiated through banks or payment providers may result in account suspension</li>
                                    <li>Koova reserves the right to recover any costs associated with chargebacks caused by user misconduct</li>
                                </ul>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    10. Store Credit and Promotional Balances
                                </h2>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Store credit may be issued in lieu of cash refunds</li>
                                    <li>Promotional credits have no cash value and may be subject to expiration</li>
                                    <li>Store credit use is governed by the <em>Koova Store Credit Terms</em></li>
                                </ul>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    11. Fraud Prevention
                                </h2>
                                <p>Koova reserves the right to:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Delay, suspend, or cancel transactions suspected of fraud</li>
                                    <li>Request additional verification</li>
                                    <li>Restrict or terminate accounts involved in fraudulent or abusive activity</li>
                                </ul>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    12. Payment Provider Limitations
                                </h2>
                                <p>Koova does not control and is not responsible for:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Downtime or errors of third-party payment providers</li>
                                    <li>Fees charged by banks or mobile money operators</li>
                                    <li>Disputes between users and payment providers</li>
                                </ul>

                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white border-b-2 border-secondary pb-2 mt-10 uppercase">
                                    13. Changes to This Policy
                                </h2>
                                <p>
                                    Koova may update this Payments & Billing Policy at any time. Updates take effect upon publication.
                                </p>
                                <p>
                                    Continued use of the Platform constitutes acceptance of the revised Policy.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    )
}

export default PaymentsnBillingPage